/**
 * Feature Flags System
 * Hierarchy: URL override → localStorage → admin_settings → default
 */

const CACHE_KEY = 'flags_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const FLAG_KEYS = {
  BIN_CTA_COPY: 'exp_bin_cta_copy',
  PRICE_FRAMING: 'exp_price_framing',
  OFFER_FLOOR_PCT: 'exp_offer_floor_pct',
  DEV_DISCOUNT: 'exp_dev_discount',
} as const;

export const FLAG_VALUES = {
  BIN_CTA: {
    CONTROL_BUY_NOW: 'CONTROL_BUY_NOW',
    VARIANT_GET_IT_NOW: 'VARIANT_GET_IT_NOW',
  },
  PRICE_FRAMING: {
    MO_ONLY: 'MO_ONLY',
    MO_PLUS_DAY: 'MO_PLUS_DAY',
  },
  OFFER_FLOOR: {
    PCT_80: '80',
    PCT_85: '85',
    PCT_90: '90',
  },
  DEV_DISCOUNT: {
    ON: 'ON',
    OFF: 'OFF',
  },
} as const;

interface FlagsCache {
  data: Record<string, string>;
  timestamp: number;
}

let flagsCache: FlagsCache | null = null;

/**
 * Get flag from URL parameters
 */
function getUrlOverride(key: string): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get(key);
}

/**
 * Get flag from localStorage
 */
function getLocalStorageOverride(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(`flag:${key}`);
  } catch {
    return null;
  }
}

/**
 * Set flag in localStorage
 */
export function setLocalStorageFlag(key: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null || value === 'off') {
      localStorage.removeItem(`flag:${key}`);
    } else {
      localStorage.setItem(`flag:${key}`, value);
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Fetch flags from admin settings API
 */
async function fetchAdminFlags(): Promise<Record<string, string>> {
  try {
    const functionsBase =
      import.meta.env.VITE_FUNCTIONS_BASE ||
      'https://ifxetmmcscffwlijpdfs.supabase.co/functions/v1';

    const keys = Object.values(FLAG_KEYS).join(',');
    const response = await fetch(`${functionsBase}/admin-settings-get?keys=${keys}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-admin': '1',
      },
    });

    if (!response.ok) {
      console.warn('Failed to fetch admin flags:', response.status);
      return {};
    }

    return await response.json();
  } catch (error) {
    console.warn('Error fetching admin flags:', error);
    return {};
  }
}

/**
 * Get cached admin flags or fetch fresh
 */
async function getAdminFlags(): Promise<Record<string, string>> {
  // Check cache
  if (flagsCache && Date.now() - flagsCache.timestamp < CACHE_TTL) {
    return flagsCache.data;
  }

  // Fetch fresh
  const data = await fetchAdminFlags();
  flagsCache = { data, timestamp: Date.now() };
  return data;
}

/**
 * Get a feature flag value
 * Priority: URL override > localStorage > admin_settings > default
 */
export async function getFlag(key: string, defaultValue: string): Promise<string> {
  // Check URL override
  const urlOverride = getUrlOverride(key);
  if (urlOverride && urlOverride !== 'off') {
    return urlOverride;
  }

  // Check localStorage
  const localOverride = getLocalStorageOverride(key);
  if (localOverride) {
    return localOverride;
  }

  // Check admin settings
  const adminFlags = await getAdminFlags();
  if (adminFlags[key]) {
    return adminFlags[key];
  }

  // Return default
  return defaultValue;
}

/**
 * Get all flags at once (for analytics snapshot)
 */
export async function getAllFlags(): Promise<Record<string, string>> {
  const [binCta, priceFraming, offerFloor, devDiscount] = await Promise.all([
    getFlag(FLAG_KEYS.BIN_CTA_COPY, FLAG_VALUES.BIN_CTA.CONTROL_BUY_NOW),
    getFlag(FLAG_KEYS.PRICE_FRAMING, FLAG_VALUES.PRICE_FRAMING.MO_PLUS_DAY),
    getFlag(FLAG_KEYS.OFFER_FLOOR_PCT, FLAG_VALUES.OFFER_FLOOR.PCT_80),
    getFlag(FLAG_KEYS.DEV_DISCOUNT, FLAG_VALUES.DEV_DISCOUNT.OFF),
  ]);

  return {
    binCta,
    priceFraming,
    offerFloor,
    devDiscount,
  };
}

/**
 * Clear flags cache (useful after admin updates)
 */
export function clearFlagsCache(): void {
  flagsCache = null;
}

/**
 * Handle QA URL overrides and clean up URL
 */
export function handleQaOverrides(): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const params = url.searchParams;
  let changed = false;

  // Handle each flag
  Object.values(FLAG_KEYS).forEach((key) => {
    const value = params.get(key);
    if (value) {
      setLocalStorageFlag(key, value);
      params.delete(key);
      changed = true;
    }
  });

  // Update URL if we removed any params
  if (changed) {
    window.history.replaceState({}, '', url.toString());
  }
}

/**
 * Get BIN CTA label based on flag
 */
export async function getBinCtaLabel(): Promise<string> {
  const variant = await getFlag(FLAG_KEYS.BIN_CTA_COPY, FLAG_VALUES.BIN_CTA.CONTROL_BUY_NOW);
  return variant === FLAG_VALUES.BIN_CTA.VARIANT_GET_IT_NOW ? 'Get It Now' : 'Buy Now';
}

/**
 * Get offer floor percentage
 */
export async function getOfferFloorPct(): Promise<number> {
  const pct = await getFlag(FLAG_KEYS.OFFER_FLOOR_PCT, FLAG_VALUES.OFFER_FLOOR.PCT_80);
  return parseInt(pct, 10) || 80;
}

/**
 * Check if price framing should show daily
 */
export async function shouldShowDailyPrice(): Promise<boolean> {
  const variant = await getFlag(FLAG_KEYS.PRICE_FRAMING, FLAG_VALUES.PRICE_FRAMING.MO_PLUS_DAY);
  return variant === FLAG_VALUES.PRICE_FRAMING.MO_PLUS_DAY;
}

/**
 * Check if dev discount should be shown
 */
export async function shouldShowDevDiscount(): Promise<boolean> {
  const variant = await getFlag(FLAG_KEYS.DEV_DISCOUNT, FLAG_VALUES.DEV_DISCOUNT.OFF);
  return variant === FLAG_VALUES.DEV_DISCOUNT.ON;
}
